/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/emailFlow.scss';

import React, {KeyboardEventHandler, MouseEventHandler, useContext, useMemo, useState} from 'react';
import {AwesomeIcon, ChangeFunction, ElementPosition, Link, Loader} from '@dvrd/dvr-controls';
import EmailStep from "../../models/emailStepModel";
import EmailTemplate from "../../models/emailTemplateModel";
import {Icon} from '@iconify/react';
import checkAll from '@iconify/icons-bi/check-all';
import pauseCircle from '@iconify/icons-feather/pause-circle';
import Step from "./emailStep/step";
import classNames from 'classnames';
import {getMenuWidth} from "../../util/menuUtil";
import BolmateSwitch from "../controls/switch/bolmateSwitch";
import {translateEmailStepError} from "../../util/bolValidation";
import {EMAIL_SETTINGS_ROUTE, NO_REPLY_FLEX_KEY} from "../../util/constants";
import {AppContext} from "../misc/appContext";
import InfoBanner from "../misc/infoBanner";
import {BolmateButton, BolmateInput} from "../controls";
import {AccessKey, EmailStatLoader, EmailStepStatTotal} from "../../util/interfaces";
import useRight from "../misc/useRight";
import {numberTrim} from '../../util/utils';
import {EmailStepError} from "../../util/types";

interface Props {
    onReplaceStep: (step: EmailStep) => void;
    onDeleteStep: (step: EmailStep) => void;
    onOpenStats: (step?: EmailStep) => MouseEventHandler;
    onClickTemplate: (step: EmailStep) => MouseEventHandler;
    onChangeActive: ChangeFunction<boolean>;
    onAddStep: MouseEventHandler;
    onGlobalLoad: (loading: boolean) => void;
    onSendHistoryMails: MouseEventHandler;
    onCloseSendHistoryMails: MouseEventHandler;
    onClickImport: MouseEventHandler;
    allowChange: (step: EmailStep) => boolean;
    onNavigateTemplate?: (template: EmailTemplate, step: EmailStep, returnPage?: string) => void;
    onChangeSearch: ChangeFunction<string>;
    onSearch: MouseEventHandler & KeyboardEventHandler;
    onLoadStepStats: (loader: EmailStatLoader) => void;
    onCancelEmails: MouseEventHandler;
    onActivationChanged: VoidFunction;
    loading: boolean;
    steps: Array<EmailStep>;
    templates: Array<EmailTemplate>;
    flowIsActive: boolean;
    warnChange: boolean;
    search: string;
    globalStats: EmailStepStatTotal;
    hasPlannedEmails: boolean;
    errors: Array<EmailStepError>;
}

export default function EmailStepFlow(props: Props) {
    const context = useContext(AppContext);
    const [canEditAuto, canEdit, canCopy, canAccessEmailSettings] = useRight(AccessKey.EMAIL_EDIT_AUTOMATIC,
        AccessKey.EMAIL_EDIT, AccessKey.EMAIL_COPY, AccessKey.PAGE_SETTINGS_EMAILS);
    const {account, accounts} = context.accountContext;
    const {
        loading, onOpenStats, steps, onReplaceStep, templates, onDeleteStep, errors,
        flowIsActive, onChangeActive, allowChange, warnChange, onAddStep, onGlobalLoad, onClickTemplate, globalStats,
        onSendHistoryMails, onCloseSendHistoryMails, onClickImport, onNavigateTemplate, search, onSearch,
        onChangeSearch, onLoadStepStats, onCancelEmails, hasPlannedEmails, onActivationChanged
    } = props;
    const [searchVisible, setSearchVisible] = useState(false);
    const [autoIcon, autoText] = useMemo(() => {
        if (flowIsActive) return [<Icon icon={checkAll} className='enabled-icon'/>, 'Automatisch mailen aan'];
        return [<Icon icon={pauseCircle} className='disabled-icon'/>, 'Automatisch mailen uit']
    }, [flowIsActive]);

    function onToggleSearch() {
        setSearchVisible(true);
    }

    function renderToggles() {
        return (
            <div className='toggles-container'>
                <div className={classNames('toggle-container auto-mail', warnChange && 'warn')}>
                    <div className='row'>
                        <label className='toggle-text'>{autoIcon} {autoText}</label>
                    </div>
                    <div className='row'>
                        <label>Status</label>
                        <BolmateSwitch value={flowIsActive} onChange={onChangeActive}
                                       labels={[flowIsActive ? 'AAN' : 'UIT']}
                                       className='switch' labelClassName='switch-label' disabled={!canEditAuto}/>
                    </div>
                </div>
            </div>
        )
    }

    function renderHistoryButton() {
        if (!canEdit || !account?.settings || !account.historyImported || account.settings.historySent) return <div/>;
        if (account.getFlex('history_mails_denied') === true) return <div/>;
        return (
            <div>
                <BolmateButton onClick={onSendHistoryMails}
                               label='Bulk mailing naar 30 dagen import...'
                               className='history-mail-btn' ornaments={[{element: <AwesomeIcon name='envelope'/>},
                    {
                        element: <AwesomeIcon name='times' className='close-btn' onClick={onCloseSendHistoryMails}/>,
                        placement: ElementPosition.RIGHT
                    }]}/>
            </div>
        )
    }

    function renderImportButton() {
        if (!canCopy || accounts.length <= 1) return null;
        return (
            <Link onClick={onClickImport} disabled={loading} className='btn-copy'><AwesomeIcon
                name='copy'/>&nbsp;&nbsp;Kopiëren uit ander account...</Link>
        )
    }

    function renderStep(step: EmailStep, index: number) {
        const stepErrors = errors.filter((error: EmailStepError) => {
            if (error.reason === 'content') return error.step_id === step.id;
            else if (error.reason === 'overlap') return error.step1_id === step.id;
            return false;
        })
        return (
            <Step key={index} onReplaceStep={onReplaceStep} onDeleteStep={onDeleteStep} onOpenStats={onOpenStats}
                  onClickTemplate={onClickTemplate(step)} allowChange={allowChange} step={step} templates={templates}
                  index={index + 1} showWarning={!!stepErrors.length} errors={stepErrors} onGlobalLoad={onGlobalLoad}
                  onNavigateTemplate={onNavigateTemplate} onLoadStepStats={onLoadStepStats}
                  onActivationChanged={onActivationChanged}/>
        )
    }

    function renderTrackingDisabledInfo() {
        const enabled = account?.getFlex(NO_REPLY_FLEX_KEY) === true
        if (enabled) return null;
        const message = 'Er worden geen open- en click-aantallen bijgehouden vanwege geen gebruik no-reply e-mailadres.';
        return (
            <InfoBanner
                persistDismiss={false}
                message={message}
                actionLabel={canAccessEmailSettings ? 'Meer informatie' : undefined}
                actionRoute={canAccessEmailSettings ? EMAIL_SETTINGS_ROUTE : undefined}
            />
        )
    }

    function renderMessages() {
        const trackingEnabled = account?.getFlex(NO_REPLY_FLEX_KEY) === true;
        const firstOverlapError = errors.find((error: EmailStepError) => error.reason === 'overlap');
        if (!firstOverlapError && trackingEnabled) return null;
        return (
            <div className='texts-container'>
                {!!errors.length && (
                    <div className='warn-policy-container'>
                        <AwesomeIcon name='exclamation-circle' className='warn-icon'/>
                        <label className='warn-policy'>Je e-mails voldoen niet aan de <Link
                            route='https://partnerplatform.bol.com/nl/hulp-nodig/prestaties/beleidspunten-zelfpromotie/'>beleidspunten</Link> van
                            Bol. {translateEmailStepError(firstOverlapError, true)}</label>
                    </div>
                )}
                {renderTrackingDisabledInfo()}
            </div>
        );
    }

    function renderSearch() {
        return (
            <div className='search-container'>
                {searchVisible ? (
                    <>
                        <BolmateInput onChange={onChangeSearch} value={search} onEnter={onSearch} label='Zoeken'
                                      inputProps={{placeholder: 'Zoek een e-mail'}}
                                      ornaments={{element: <AwesomeIcon name='search'/>}} className='search-field'/>
                        <BolmateButton onClick={onSearch} label='Zoeken' secondary/>
                    </>
                ) : (
                    <>
                        <Link onClick={onToggleSearch} disabled={loading} className='btn-copy'><AwesomeIcon
                            name='search'/>&nbsp;&nbsp;E-mails zoeken</Link>
                    </>
                )}
                {renderImportButton()}
                {(!flowIsActive && hasPlannedEmails) &&
                    <Link onClick={onCancelEmails} className='cancel-link' disabled={loading}><AwesomeIcon
                        name='times'/>&nbsp;&nbsp;Ingeplande e-mails annuleren…</Link>}
            </div>
        );
    }

    function renderGlobalStats() {
        if (steps.length < 2) return null;
        const {SENT, OPEN, CLICK} = globalStats;
        const loading = SENT === -1;
        return (
            <div className='email-stats-button global' onClick={onOpenStats()}>
                <Loader active={loading} size='3rem'/>
                <div className='stat-container'>
                    <label>Verstuurd</label>
                    <label>{loading ? '-' : numberTrim(SENT)}</label>
                </div>
                <AwesomeIcon name='arrow-up-right-from-square' className='icon-container'/>
                <div className='stat-container'>
                    <label>Open</label>
                    <label>{loading ? '-' : (SENT === 0 ? 0 : (OPEN / SENT * 100).toFixed(1))}%</label>
                </div>
                <div className='stat-container'>
                    <label>Klik</label>
                    <label>{loading ? '-' : numberTrim(CLICK)}</label>
                </div>
            </div>
        )
    }

    function renderAddButton() {
        if (!canEdit) return null;
        return (
            <div className='add-step' onClick={onAddStep}>
                <div className='line top rel'/>
                <div className='plus-circle'>
                    <AwesomeIcon name='plus' className='plus-icon'/>
                </div>
                <label className='plus-label'>Voeg een mail toe aan de E-mail Volgorde</label>
            </div>
        );
    }


    const menuWidth = getMenuWidth();
    return (
        <>
            <InfoBanner featureKey='emails' variant='info'
                        title='E-mails'
                        message='Automatiseer e-mails aan jouw klanten zodat je zonder handwerk meer reviews op bol behaalt en/of facturen verstuurt.'
                        showForNewUsersOnly={true}/>
            <div className='email-flow' style={{maxWidth: `calc(100vw - ${menuWidth}px)`}}>
                <div className='flow-header-container'>
                    {renderMessages()}
                    {renderSearch()}
                    <div className='flow-header-part'>
                        {renderHistoryButton()}
                        {renderGlobalStats()}
                        {renderToggles()}
                    </div>
                </div>
                <div className='emails-container'>
                    <Loader active={loading}/>
                    {steps.map(renderStep)}
                    {renderAddButton()}
                </div>
            </div>
        </>
    )
}
